import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./HeroSection.module.css";
import hero1 from "../assests/images/hero1.png";
import hero2 from "../assests/images/hero2.png";
import hero3 from "../assests/images/hero3.png";
import trustedLeft from "../assests/images/trustedleft.png";
import trustedRight from "../assests/images/trustedright.png";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

/**
 * HeroSection Component - Ohh.me Landing Page Hero
 *
 * A modern hero section featuring trust indicators, dynamic headline,
 * phone mockups in bento-box layout, and username input for onboarding.
 * 
 * Features:
 * - Smooth fan-out hover animations on phone cards
 * - Scroll-based stack animations for content
 * - 3D perspective transforms
 * - Responsive mobile design
 *
 * @param {Object} props - Component props
 * @param {string} [props.headline] - Main headline text
 * @param {string} [props.description] - Description text below mockups
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {Function} [props.onContinue] - Callback for Continue button click
 *
 * @example
 * <HeroSection
 *   headline="Your fans Your space, Your rules."
 *   description="Ohh.me helps creators turn followers into supporters..."
 *   placeholder="Username"
 *   onContinue={(username) => console.log('Continue with:', username)}
 * />
 */
const HeroSection = ({
  headline = "Your fans Your space, Your rules.",
  description = "Ohh.me helps creators turn followers into supporters with mini-sites, shoutouts, and tools that just work.",
  placeholder = "Username",
  onContinue = () => {},
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const centerCardRef = useRef(null);
  const descriptionRef = useRef(null);
  const inputWrapperRef = useRef(null);
  

  useEffect(() => {
    // Create a GSAP context for proper cleanup
    const ctx = gsap.context(() => {
      // Modern entrance animations with enhanced easing
      
      // Trust badge - smooth fade in with scale
      gsap.from(`.${styles.trustBadge}`, {
        y: -40,
        opacity: 0,
        scale: 0.9,
        duration: 1,
        ease: "back.out(1.7)",
      });

      // Headline - split words animation
      const headlineWords = document.querySelectorAll(`.${styles.headline}`);
      gsap.from(headlineWords, {
        y: 100,
        opacity: 0,
        rotationX: -90,
        transformOrigin: "50% 100%",
        duration: 1.2,
        stagger: 0.1,
        delay: 0.3,
        ease: "power4.out",
      });

      // Phone cards - dramatic entrance with rotation and scale
      gsap.fromTo(leftCardRef.current, 
        {
          x: -200,
          y: 100,
          opacity: 0,
          rotation: -15,
          scale: 0.8,
        },
        {
          x: 0,
          y: 0,
          opacity: 1,
          rotation: 0,
          scale: 1,
          duration: 1.4,
          delay: 0.6,
          ease: "power3.out",
          clearProps: "transform",
        }
      );

      gsap.fromTo(centerCardRef.current,
        {
          y: 150,
          opacity: 0,
          scale: 0.85,
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.4,
          delay: 0.7,
          ease: "power3.out",
          clearProps: "all",
          onComplete: () => {
            if (centerCardRef.current) {
              centerCardRef.current.style.transform = 'none';
            }
          }
        }
      );

      gsap.fromTo(rightCardRef.current,
        {
          x: 200,
          y: 100,
          opacity: 0,
          rotation: 15,
          scale: 0.8,
        },
        {
          x: 0,
          y: 0,
          opacity: 1,
          rotation: 0,
          scale: 1,
          duration: 1.4,
          delay: 0.8,
          ease: "power3.out",
          clearProps: "transform",
        }
      );

      // Check if desktop for scroll animations
      const isDesktop = window.innerWidth > 768;
      
      if (isDesktop) {
        // Parallax for side cards on scroll
        gsap.to(leftCardRef.current, {
          y: -30,
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        });

        gsap.to(rightCardRef.current, {
          y: -30,
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        });

        // Stack animation for description - comes up smoothly on scroll
        gsap.fromTo(descriptionRef.current,
          {
            y: 100,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 1.2,
            delay: 1.2,
            ease: "power3.out",
            scrollTrigger: {
              trigger: centerCardRef.current,
              start: "center center",
              end: "bottom center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );

        // Stack animation for input wrapper - comes up after description
        gsap.fromTo(inputWrapperRef.current,
          {
            y: 120,
            opacity: 0,
            scale: 0.95,
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 1.2,
            delay: 1.3,
            ease: "power3.out",
            scrollTrigger: {
              trigger: centerCardRef.current,
              start: "center center",
              end: "bottom center",
              scrub: 1.2,
              toggleActions: "play none none reverse",
            },
          }
        );
      } else {
        // Simple fade in for mobile (no scroll effects)
        gsap.fromTo(descriptionRef.current,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, delay: 1.2, ease: "power3.out" }
        );
        
        gsap.fromTo(inputWrapperRef.current,
          { y: 60, opacity: 0, scale: 0.95 },
          { y: 0, opacity: 1, scale: 1, duration: 1, delay: 1.3, ease: "back.out(1.7)" }
        );
      }

    }, containerRef);

    // Cleanup function to kill animations when component unmounts
    return () => ctx.revert();
  }, []);

  /**
   * Handle continue button click
   * Extracts username from input and passes to callback
   */
  const handleContinue = () => {
    const username = inputRef.current?.value || "";
    onContinue(username);
  };

  /**
   * Handle Enter key press in input field
   */
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleContinue();
    }
  };

  return (
    <div ref={containerRef} className={styles.hero}>
      <div className={styles.container}>
        {/* Trust Badge Section */}
        <div className={styles.trustBadge}>
          <div className={styles.avatarGroup}>
            <img src={trustedLeft} alt="Trusted creators" className={styles.avatarImage} />
            <span className={styles.badge}>Trusted by 10,000+ Creators.</span>
            <img src={trustedRight} alt="Trusted creators" className={styles.avatarImage} />
          </div>
        </div>

        {/* Main Headline */}
        <h1 className={styles.headline}>
          Your fans<br />
          Your space,<br />
          Your rules.
        </h1>

        {/* Phone Mockups in Bento Layout */}
        <div className={styles.phoneMockups}>
          {/* Left Phone Stack */}
          <div className={styles.phoneStack}>
            {/* Main left card */}
            <div ref={leftCardRef} className={`${styles.phoneCard} ${styles.phoneLeft}`}>
              <div className={styles.phoneFrame}>
                <img src={hero1} alt="Creator profile example" />
              </div>
            </div>
          </div>

          {/* Center Phone - Tall, with profile overlay */}
          <div 
            ref={centerCardRef} 
            className={`${styles.phoneCard} ${styles.phoneCenter}`}
          >
            <div className={styles.phoneFrame}>
              <img src={hero2} alt="Creator content example" />
              
              {/* Profile Overlay */}
              <div className={styles.profileOverlay}>
                <div className={styles.profileInfo}>
                  <img 
                    src="https://i.pravatar.cc/150?img=12" 
                    alt="Profile avatar" 
                    className={styles.profileAvatar}
                  />
                  <span className={styles.profileUsername}>@Samiiiolle</span>
                </div>

                <div className={styles.socialIcons}>
                  {/* Instagram Icon */}
                  <div className={styles.socialIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.206 2.024c-1.013-.028-2.137-.027-3.153.04-2.023.135-3.775 1.005-4.87 2.1-1.095 1.095-1.965 2.847-2.1 4.87-.135 2.02-.135 4.123 0 6.143.135 2.023 1.005 3.775 2.1 4.87 1.095 1.095 2.847 1.965 4.87 2.1 2.02.135 4.123.135 6.143 0 2.023-.135 3.775-1.005 4.87-2.1 1.095-1.095 1.965-2.847 2.1-4.87.135-2.02.135-4.123 0-6.143-.135-2.023-1.005-3.775-2.1-4.87-1.095-1.095-2.847-1.965-4.87-2.1-1.016-.067-2.14-.068-3.153-.04zm-.043 1.5c.98-.028 2.07-.026 3.046.036 1.81.122 3.25.85 4.172 1.772.922.922 1.65 2.362 1.772 4.172.122 1.807.122 3.903 0 5.71-.122 1.81-.85 3.25-1.772 4.172-.922.922-2.362 1.65-4.172 1.772-1.807.122-3.903.122-5.71 0-1.81-.122-3.25-.85-4.172-1.772-.922-.922-1.65-2.362-1.772-4.172-.122-1.807-.122-3.903 0-5.71.122-1.81.85-3.25 1.772-4.172.922-.922 2.362-1.65 4.172-1.772.976-.062 2.066-.064 3.046-.036zM12 7a5 5 0 100 10 5 5 0 000-10zm0 1.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm5.25-2.5a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5z"/>
                    </svg>
                  </div>
                  {/* TikTok Icon */}
                  <div className={styles.socialIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </div>
                  {/* Snapchat Icon */}
                  <div className={styles.socialIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path clipRule="evenodd" d="M12.131.5h.566v.021c1.37.108 4.208.731 5.64 3.938v.001c.295.665.393 1.518.414 2.356.021.848-.035 1.745-.085 2.52l-.003.063-.001.021c.175-.032.388-.102.622-.215.26-.135.522-.153.67-.153.218 0 .449.034.66.117.552.188 1.027.644 1.031 1.264.01.387-.166.715-.433.972-.26.25-.62.45-1.043.62l-.015.006-.016.005a5.17 5.17 0 0 0-.12.042l-.016.006c-.056.02-.122.043-.194.066l-.01.004c-.216.065-.45.14-.647.241-.203.104-.287.194-.313.248-.012.035-.028.144.064.344l.023.023.038.087c.025.056.367.833 1.067 1.655.705.829 1.742 1.667 3.148 1.899h.004c.476.083.84.505.817.996-.002.136-.03.264-.078.384l-.004.011c-.195.463-.661.766-1.194.981-.49.198-1.128.359-1.903.489-.014.055-.026.113-.04.17l-.005.026c-.032.192-.08.382-.14.576a1.014 1.014 0 0 1-.378.555 1.07 1.07 0 0 1-.633.191h-.03c-.185 0-.405-.04-.609-.08l-.008-.001a5.28 5.28 0 0 0-1.118-.12c-.27 0-.523.014-.782.063l-.007.001c-.446.078-.855.348-1.45.764l-.035.025c-.809.568-1.85 1.3-3.409 1.3-.06 0-.117-.007-.143-.01h-.005a1.023 1.023 0 0 0-.028-.004h-.139c-1.579 0-2.613-.738-3.432-1.327-.593-.416-.985-.682-1.425-.76a5.84 5.84 0 0 0-.81-.066 5.26 5.26 0 0 0-1.123.134c-.199.04-.416.08-.614.08-.287 0-.534-.09-.723-.255a1.081 1.081 0 0 1-.313-.498 3.461 3.461 0 0 1-.098-.398 4.4 4.4 0 0 0-.039-.172 7.173 7.173 0 0 1-.035-.148l-.003-.01-.012-.054c-.792-.107-1.44-.255-1.937-.449-.54-.21-1.003-.514-1.2-.98a1.08 1.08 0 0 1-.095-.393.975.975 0 0 1 .816-1.006h.004c1.406-.233 2.442-1.071 3.147-1.9a7.25 7.25 0 0 0 1.067-1.658l.01-.023.023-.043c.143-.275.104-.38.102-.384l-.003-.008c-.021-.046-.102-.138-.319-.249a4.799 4.799 0 0 0-.641-.25 2.663 2.663 0 0 1-.381-.132c-.571-.225-.964-.491-1.202-.803a1.216 1.216 0 0 1-.249-.966l.002-.006c.076-.408.353-.702.638-.883a1.86 1.86 0 0 1 .972-.285c.21 0 .392.043.552.107l.013.005.012.005c.32.148.57.22.76.237l-.01-.111-.002-.047C5.267 8.51 5.214 7.63 5.236 6.8c.022-.836.12-1.687.416-2.349C7.247.798 10.719.516 11.729.514L12.131.5Z" fillRule="evenodd" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Phone Stack */}
          <div className={styles.phoneStack}>
            {/* Main right card */}
            <div ref={rightCardRef} className={`${styles.phoneCard} ${styles.phoneRight}`}>
              <div className={styles.phoneFrame}>
                <img src={hero3} alt="Creator showcase example" />
              </div>
            </div>
          </div>
        </div>

        {/* Description Text */}
        <p ref={descriptionRef} className={styles.description}>
          {description}
        </p>

        {/* Username Input and Continue Button */}
        <div ref={inputWrapperRef} className={styles.inputWrapper}>
          <div className={styles.inputContainer}>
            <span className={styles.inputPrefix}>@ohh.me/</span>
            <input
              ref={inputRef}
              type="text"
              placeholder={placeholder}
              className={styles.input}
              onKeyPress={handleKeyPress}
            />
          </div>
          <button className={styles.continueBtn} onClick={handleContinue}>
            Continue <span className={styles.arrow}>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;